import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  GripVertical,
  ImagePlus,
  Link2,
  Loader2,
  PlayCircle,
  RotateCcw,
  Star,
  Trash2,
  UploadCloud,
  Video,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { dictionary, localizePath } from "@/i18n/translations";
import { api, buildAuthHeaders, resolveImageUrl } from "@/lib/api";
import { setDocumentMeta } from "@/lib/seo";
import { getRegionEntries } from "@/pages/dashboard/dashboardUtils";
import { PropertyLocationPickerSection } from "@/components/maps/PropertyLocationPickerSection";
import { extractPropertyCoordinates } from "@/lib/propertyMapUtils";
import {
  getImageUploadErrorMessageKey,
  optimizeImageFile,
} from "@/lib/imageUploadUtils";
import { getVideoUploadErrorMessageKey, validateVideoFile } from "@/lib/videoUploadUtils";

const LAND_PROPERTY_TYPES = new Set([
  "land",
  "residential-land",
  "commercial-land",
  "agricultural-land",
]);

const ROOM_PROPERTY_TYPES = new Set([
  "apartment",
  "villa",
  "building",
  "palace",
  "rest-house",
  "floor-in-villa",
  "floor-in-building",
  "commercial",
]);

const ELEVATOR_PROPERTY_TYPES = new Set(["villa", "building"]);

const PROPERTY_AGE_TYPES = new Set([
  "villa",
  "apartment",
  "building",
  "palace",
  "rest-house",
  "floor-in-villa",
  "floor-in-building",
  "shop",
]);
const FLOORS_COUNT_PROPERTY_TYPES = new Set(["villa", "building", "palace", "shop"]);
const UNITS_COUNT_PROPERTY_TYPES = new Set(["building", "shop"]);
const REQUIRED_UNITS_COUNT_PROPERTY_TYPES = new Set(["shop"]);
const STREET_FRONTAGE_PROPERTY_TYPES = new Set(["shop"]);
const REQUIRED_STREET_FRONTAGE_PROPERTY_TYPES = new Set(["shop"]);

const PROPERTY_TYPE_OPTIONS = [
  "villa",
  "floor-in-villa",
  "apartment",
  "building",
  "floor-in-building",
  "palace",
  "rest-house",
  "shop",
  "residential-land",
  "commercial-land",
  "agricultural-land",
  "land",
  "commercial",
];

const STREET_FRONTAGE_OPTIONS = ["single-street", "two-streets", "three-streets", "corner", "main-road"];

const createInitialForm = () => ({
  title_ar: "",
  title_en: "",
  description_ar: "",
  description_en: "",
  location_ar: "",
  location_en: "",
  region_code: "makkah",
  sub_area_code: "makkah-city",
  listing_type: "sale",
  property_type: "villa",
  availability: "available",
  price: "",
  bedrooms: "",
  bathrooms: "",
  living_rooms: "",
  parking_spots: "",
  floors_count: "",
  units_count: "",
  has_elevator: null,
  property_age: "",
  area_sqm: "",
  street_frontage_type: "",
  zoning: "",
  is_visible: true,
  contact_phone: "",
  latitude: null,
  longitude: null,
  advanced_image_urls: "",
  video_url: "",
  video_thumbnail_url: "",
  video_preview_url: "",
});

const createImageId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `img-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const buildExistingImageItems = (sources = []) =>
  sources.map((source) => ({
    id: createImageId(),
    kind: "existing",
    source,
    previewUrl: resolveImageUrl(source),
    file: null,
    name: source.split("/").pop() || "image",
    size: 0,
    status: "success",
    errorMessage: "",
  }));

const revokePendingPreviewUrl = (item) => {
  if (item?.kind === "pending" && item?.previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(item.previewUrl);
  }
};

const revokeVideoPreviewUrl = (previewUrl) => {
  if (previewUrl?.startsWith("blob:")) {
    URL.revokeObjectURL(previewUrl);
  }
};

const arraysEqual = (first, second) =>
  first.length === second.length && first.every((value, index) => value === second[index]);

const parseAdvancedUrls = (inputValue) =>
  inputValue
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean)
    .filter((item) => item.startsWith("https://") || item.startsWith("/uploads/"));

const extractUploadedPaths = (beforeImages, afterImages) => {
  const beforeCounts = new Map();
  beforeImages.forEach((item) => {
    beforeCounts.set(item, (beforeCounts.get(item) || 0) + 1);
  });

  const uploaded = [];
  afterImages.forEach((item) => {
    const count = beforeCounts.get(item) || 0;
    if (count > 0) {
      beforeCounts.set(item, count - 1);
    } else {
      uploaded.push(item);
    }
  });

  return uploaded;
};

const parseOptionalCoordinate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  const normalized = value
    .toString()
    .trim()
    .replace(/[،]/g, ".")
    .replace(/,/g, ".");

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parsed;
};

const parseOptionalInteger = (value) => {
  const normalized = (value || "").toString().trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }

  return Math.trunc(parsed);
};

export default function DashboardAddPropertyPage({ lang }) {
  const copy = dictionary[lang];
  const { session } = useAuth();
  const navigate = useNavigate();
  const { propertyId } = useParams();
  const [searchParams] = useSearchParams();

  const isEditMode = Boolean(propertyId);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [workingPropertyId, setWorkingPropertyId] = useState(propertyId || "");
  const [form, setForm] = useState(createInitialForm());
  const [imageItems, setImageItems] = useState([]);
  const [draggingImageId, setDraggingImageId] = useState("");
  const [dropzoneActive, setDropzoneActive] = useState(false);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [videoDropzoneActive, setVideoDropzoneActive] = useState(false);
  const [videoPreviewModalOpen, setVideoPreviewModalOpen] = useState(false);
  const [videoPendingFile, setVideoPendingFile] = useState(null);
  const [videoPendingPreviewUrl, setVideoPendingPreviewUrl] = useState("");
  const [videoMarkedForRemoval, setVideoMarkedForRemoval] = useState(false);
  const [uploadState, setUploadState] = useState({
    status: "idle",
    progress: 0,
    message: "",
  });
  const [videoUploadState, setVideoUploadState] = useState({
    status: "idle",
    progress: 0,
    message: "",
  });

  const fileInputRef = useRef(null);
  const videoFileInputRef = useRef(null);
  const imageItemsRef = useRef([]);
  const videoRemovalRef = useRef(false);

  useEffect(() => {
    setWorkingPropertyId(propertyId || "");
  }, [propertyId]);

  useEffect(() => {
    document.body.classList.add("dashboard-property-overlay-fix");
    return () => {
      document.body.classList.remove("dashboard-property-overlay-fix");
    };
  }, []);

  useEffect(() => {
    imageItemsRef.current = imageItems;
  }, [imageItems]);

  useEffect(
    () => () => {
      imageItemsRef.current.forEach((item) => revokePendingPreviewUrl(item));
    },
    [],
  );

  useEffect(
    () => () => {
      revokeVideoPreviewUrl(videoPendingPreviewUrl);
    },
    [videoPendingPreviewUrl],
  );

  const regions = useMemo(() => getRegionEntries(copy.regions), [copy.regions]);
  const isLandPropertyType = LAND_PROPERTY_TYPES.has(form.property_type);
  const showsStructuredRoomFields = ROOM_PROPERTY_TYPES.has(form.property_type);
  const showsElevatorField = ELEVATOR_PROPERTY_TYPES.has(form.property_type);
  const showsPropertyAgeField = PROPERTY_AGE_TYPES.has(form.property_type);
  const showsFloorsCountField = FLOORS_COUNT_PROPERTY_TYPES.has(form.property_type);
  const showsUnitsCountField = UNITS_COUNT_PROPERTY_TYPES.has(form.property_type);
  const showsStreetFrontageField =
    isLandPropertyType || STREET_FRONTAGE_PROPERTY_TYPES.has(form.property_type);
  const propertyTypeLabels = copy.advancedSearch?.propertyTypes || {};
  const unitsCountLabel =
    form.property_type === "shop"
      ? copy.dashboard.shopsCount || copy.properties.shopsCount || copy.properties.unitsCount
      : copy.dashboard.unitsCount || copy.properties.unitsCount;
  const mapPickerCopy = copy.dashboardMapPicker || {
    sectionTitle: "Property location on map",
    sectionSubtitle: "Click on the map to place a pin, drag to refine, or search by address.",
    searchPlaceholder: "Search place or address",
    mapLoading: "Loading map...",
    latitude: "Latitude",
    longitude: "Longitude",
    clearPin: "Clear pin",
  };

  useEffect(() => {
    setDocumentMeta({
      lang,
      title: `${isEditMode ? copy.dashboardProperties.editProperty : copy.dashboardNav.addProperty} | ${copy.nav.brand}`,
      description: copy.dashboard.subtitle,
    });
  }, [copy.dashboard.subtitle, copy.dashboardNav.addProperty, copy.dashboardProperties.editProperty, copy.nav.brand, isEditMode, lang]);

  useEffect(() => {
    if (!isEditMode) {
      return;
    }

    const fetchProperty = async () => {
      setLoading(true);
      try {
        const response = await api.get("/admin/properties", {
          headers: buildAuthHeaders(session),
        });
        const found = (response.data || []).find((item) => item.id === propertyId);
        if (!found) {
          toast.error(copy.dashboardProperties.propertyNotFound);
          navigate(localizePath(lang, "/dashboard/properties"));
          return;
        }

        const existingCoordinates = extractPropertyCoordinates(found);

        setForm({
          title_ar: found.title?.ar || "",
          title_en: found.title?.en || "",
          description_ar: found.description?.ar || "",
          description_en: found.description?.en || "",
          location_ar: found.location?.ar || "",
          location_en: found.location?.en || "",
          region_code: found.region_code,
          sub_area_code: found.sub_area_code || "general",
          listing_type: found.listing_type,
          property_type: found.property_type || "villa",
          availability: found.availability,
          price: String(found.price),
          bedrooms: found.bedrooms !== null && found.bedrooms !== undefined ? String(found.bedrooms) : "",
          bathrooms: found.bathrooms !== null && found.bathrooms !== undefined ? String(found.bathrooms) : "",
          living_rooms:
            found.living_rooms !== null && found.living_rooms !== undefined
              ? String(found.living_rooms)
              : "",
          parking_spots:
            found.parking_spots !== null && found.parking_spots !== undefined
              ? String(found.parking_spots)
              : "",
          floors_count:
            found.floors_count !== null && found.floors_count !== undefined
              ? String(found.floors_count)
              : "",
          units_count:
            found.units_count !== null && found.units_count !== undefined
              ? String(found.units_count)
              : "",
          has_elevator: typeof found.has_elevator === "boolean" ? found.has_elevator : null,
          property_age:
            found.property_age !== null && found.property_age !== undefined
              ? String(found.property_age)
              : "",
          area_sqm: String(found.area_sqm),
          street_frontage_type: found.street_frontage_type || "",
          zoning: found.zoning || "",
          is_visible: found.is_visible,
          contact_phone: found.contact_phone || "",
          latitude: existingCoordinates ? existingCoordinates.lat : null,
          longitude: existingCoordinates ? existingCoordinates.lng : null,
          advanced_image_urls: "",
          video_url: found.video_url || "",
          video_thumbnail_url: found.video_thumbnail_url || "",
          video_preview_url: found.video_preview_url || "",
        });
        setWorkingPropertyId(found.id);
        setImageItems(buildExistingImageItems(found.images || []));
        setVideoPendingFile(null);
        setVideoMarkedForRemoval(false);
        videoRemovalRef.current = false;
        setVideoPendingPreviewUrl((previousUrl) => {
          revokeVideoPreviewUrl(previousUrl);
          return "";
        });
      } catch {
        toast.error(copy.common.requestFailed);
      } finally {
        setLoading(false);
      }
    };

    fetchProperty();
  }, [copy.common.requestFailed, copy.dashboardProperties.propertyNotFound, isEditMode, lang, navigate, propertyId, session]);

  const goBackToList = () => {
    const returnQuery = searchParams.get("return");
    const fallback = localizePath(lang, "/dashboard/properties");
    navigate(returnQuery ? `${fallback}?${returnQuery}` : fallback);
  };

  const handleMapCoordinatesChange = useCallback(({ latitude, longitude }) => {
    setForm((prev) => ({
      ...prev,
      latitude: parseOptionalCoordinate(latitude),
      longitude: parseOptionalCoordinate(longitude),
    }));
  }, []);

  const buildPayload = () => {
    const shouldClearVideo =
      (videoMarkedForRemoval || videoRemovalRef.current) && !videoPendingFile;

    const bedrooms = showsStructuredRoomFields ? parseOptionalInteger(form.bedrooms) : null;
    const bathrooms = showsStructuredRoomFields ? parseOptionalInteger(form.bathrooms) : null;
    const livingRooms = showsStructuredRoomFields ? parseOptionalInteger(form.living_rooms) : null;
    const parkingSpots = showsStructuredRoomFields ? parseOptionalInteger(form.parking_spots) : null;
    const floorsCount = showsFloorsCountField ? parseOptionalInteger(form.floors_count) : null;
    const unitsCount = showsUnitsCountField ? parseOptionalInteger(form.units_count) : null;
    const propertyAge = showsPropertyAgeField ? parseOptionalInteger(form.property_age) : null;

    return {
      title: { ar: form.title_ar, en: form.title_en },
      description: { ar: form.description_ar, en: form.description_en },
      location: { ar: form.location_ar, en: form.location_en },
      region_code: form.region_code,
      sub_area_code: form.sub_area_code,
      listing_type: form.listing_type,
      property_type: form.property_type,
      availability: form.availability,
      price: Number(form.price),
      currency: "SAR",
      bedrooms,
      bathrooms,
      living_rooms: livingRooms,
      parking_spots: parkingSpots,
      floors_count: floorsCount,
      units_count: unitsCount,
      has_elevator: showsElevatorField ? form.has_elevator : null,
      property_age: propertyAge,
      area_sqm: Number(form.area_sqm),
      street_frontage_type: showsStreetFrontageField ? form.street_frontage_type || null : null,
      zoning: isLandPropertyType ? form.zoning || null : null,
      latitude: form.latitude,
      longitude: form.longitude,
      is_visible: form.is_visible,
      contact_phone: form.contact_phone || null,
      images: [],
      video_url: shouldClearVideo ? null : form.video_url.trim() || null,
      video_thumbnail_url: shouldClearVideo
        ? null
        : form.video_thumbnail_url.trim() || null,
      video_preview_url: shouldClearVideo
        ? null
        : form.video_preview_url.trim() || null,
    };
  };

  const uploadSingleImageFile = useCallback(
    async (targetPropertyId, imageFile, onProgress) => {
      const data = new FormData();
      data.append("files", imageFile);

      const response = await api.post(`/admin/properties/${targetPropertyId}/images`, data, {
        headers: {
          ...buildAuthHeaders(session),
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (event) => {
          if (!event.total) {
            return;
          }
          const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
          onProgress?.(progress);
        },
      });

      return response.data?.images || [];
    },
    [session],
  );

  const uploadSelectedVideo = async (targetPropertyId, pendingFile) => {
    if (!pendingFile) {
      return null;
    }

    const data = new FormData();
    data.append("file", pendingFile);

    if (form.video_thumbnail_url.trim()) {
      data.append("thumbnail_url", form.video_thumbnail_url.trim());
    }
    if (form.video_preview_url.trim()) {
      data.append("preview_url", form.video_preview_url.trim());
    }

    const response = await api.post(`/admin/properties/${targetPropertyId}/video`, data, {
      headers: {
        ...buildAuthHeaders(session),
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (event) => {
        if (!event.total) {
          return;
        }
        const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
        setVideoUploadState({
          status: "uploading",
          progress,
          message: copy.dashboardVideoUpload.uploading,
        });
      },
    });

    return response.data || null;
  };

  const appendOptimizedFiles = async (rawFiles) => {
    const files = Array.from(rawFiles || []);
    if (!files.length) {
      return;
    }

    setProcessingFiles(true);
    const nextItems = [];

    for (const rawFile of files) {
      try {
        const optimized = await optimizeImageFile(rawFile);
        const previewUrl = URL.createObjectURL(optimized);
        nextItems.push({
          id: createImageId(),
          kind: "pending",
          source: "",
          previewUrl,
          file: optimized,
          name: optimized.name,
          size: optimized.size,
          status: "idle",
          errorMessage: "",
        });
      } catch (error) {
        const key = getImageUploadErrorMessageKey(error.message);
        toast.error(copy.dashboardImageUpload.errors[key] || copy.common.requestFailed);
      }
    }

    if (nextItems.length) {
      setImageItems((prev) => [...prev, ...nextItems]);
    }

    setProcessingFiles(false);
  };

  const onFileInputChange = async (event) => {
    await appendOptimizedFiles(event.target.files);
    event.target.value = "";
  };

  const onDropFiles = async (event) => {
    event.preventDefault();
    setDropzoneActive(false);
    await appendOptimizedFiles(event.dataTransfer.files);
  };

  const selectPendingVideoFile = (rawFile) => {
    if (!rawFile) {
      return;
    }

    try {
      validateVideoFile(rawFile);
      const nextPreviewUrl = URL.createObjectURL(rawFile);
      revokeVideoPreviewUrl(videoPendingPreviewUrl);
      setVideoPendingFile(rawFile);
      setVideoPendingPreviewUrl(nextPreviewUrl);
      setVideoMarkedForRemoval(false);
      videoRemovalRef.current = false;
      setVideoUploadState({ status: "idle", progress: 0, message: "" });
      setForm((prev) => ({
        ...prev,
        video_url: "",
      }));
    } catch (error) {
      const key = getVideoUploadErrorMessageKey(error.message);
      toast.error(copy.dashboardVideoUpload.errors[key] || copy.common.requestFailed);
    }
  };

  const onVideoInputChange = (event) => {
    const [file] = Array.from(event.target.files || []);
    selectPendingVideoFile(file);
    event.target.value = "";
  };

  const onVideoDrop = (event) => {
    event.preventDefault();
    setVideoDropzoneActive(false);
    const [file] = Array.from(event.dataTransfer.files || []);
    selectPendingVideoFile(file);
  };

  const removeVideoSelection = () => {
    revokeVideoPreviewUrl(videoPendingPreviewUrl);
    setVideoPendingPreviewUrl("");
    setVideoPendingFile(null);
    setVideoMarkedForRemoval(true);
    videoRemovalRef.current = true;
    setVideoPreviewModalOpen(false);
    setVideoUploadState({ status: "idle", progress: 0, message: "" });
    setForm((prev) => ({
      ...prev,
      video_url: "",
      video_thumbnail_url: "",
      video_preview_url: "",
    }));
  };

  const moveImageItem = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) {
      return;
    }

    setImageItems((prev) => {
      const fromIndex = prev.findIndex((item) => item.id === fromId);
      const toIndex = prev.findIndex((item) => item.id === toId);
      if (fromIndex < 0 || toIndex < 0) {
        return prev;
      }

      const draft = [...prev];
      const [moved] = draft.splice(fromIndex, 1);
      draft.splice(toIndex, 0, moved);
      return draft;
    });
  };

  const moveToCover = (id) => {
    setImageItems((prev) => {
      const index = prev.findIndex((item) => item.id === id);
      if (index <= 0) {
        return prev;
      }
      const draft = [...prev];
      const [item] = draft.splice(index, 1);
      draft.unshift(item);
      return draft;
    });
  };

  const removeImageItem = (id) => {
    setImageItems((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) {
        revokePendingPreviewUrl(target);
      }
      return prev.filter((item) => item.id !== id);
    });
  };

  const buildFinalOrderedImages = useCallback((items, manualUrls) => {
    const orderedImages = items
      .filter((item) => item.source && item.status !== "failed")
      .map((item) => item.source);

    return [
      ...orderedImages,
      ...manualUrls.filter((url) => !orderedImages.includes(url)),
    ];
  }, []);

  const retryImageUpload = useCallback(
    async (imageId) => {
      const targetId = workingPropertyId || propertyId;
      if (!targetId) {
        toast.error(copy.dashboardImageUpload.uploadFailed);
        return;
      }

      const currentItems = imageItemsRef.current;
      const targetItem = currentItems.find((item) => item.id === imageId);
      if (!targetItem?.file) {
        return;
      }

      setSaving(true);
      setImageItems((prev) =>
        prev.map((item) =>
          item.id === imageId
            ? { ...item, status: "uploading", errorMessage: "" }
            : item,
        ),
      );

      try {
        const beforeImages = currentItems
          .filter((item) => item.id !== imageId && item.source)
          .map((item) => item.source);

        const afterUploadImages = await uploadSingleImageFile(
          targetId,
          targetItem.file,
          (progress) => {
            setUploadState({
              status: "uploading",
              progress,
              message: copy.dashboardImageUpload.uploading,
            });
          },
        );

        const uploadedPaths = extractUploadedPaths(beforeImages, afterUploadImages);
        const uploadedPath = uploadedPaths[0];
        if (!uploadedPath) {
          throw new Error("upload_failed");
        }

        if (targetItem.previewUrl?.startsWith("blob:")) {
          URL.revokeObjectURL(targetItem.previewUrl);
        }

        const syncedItems = imageItemsRef.current.map((item) =>
          item.id === imageId
            ? {
                ...item,
                kind: "existing",
                source: uploadedPath,
                previewUrl: resolveImageUrl(uploadedPath),
                file: null,
                status: "success",
                errorMessage: "",
              }
            : item,
        );

        const manualUrls = parseAdvancedUrls(form.advanced_image_urls);
        const finalImages = buildFinalOrderedImages(syncedItems, manualUrls);

        if (!arraysEqual(finalImages, afterUploadImages)) {
          await api.put(
            `/admin/properties/${targetId}`,
            { images: finalImages },
            { headers: buildAuthHeaders(session) },
          );
        }

        setImageItems(syncedItems);
        setUploadState({
          status: "success",
          progress: 100,
          message: copy.dashboardImageUpload.success,
        });
        toast.success(copy.dashboardImageUpload.success);
      } catch (error) {
        const detail =
          error?.response?.data?.detail || copy.dashboardImageUpload.uploadFailed;
        setImageItems((prev) =>
          prev.map((item) =>
            item.id === imageId
              ? { ...item, status: "failed", errorMessage: detail }
              : item,
          ),
        );
        setUploadState({ status: "error", progress: 0, message: detail });
        toast.error(detail);
      } finally {
        setSaving(false);
      }
    },
    [
      buildFinalOrderedImages,
      copy.dashboardImageUpload.success,
      copy.dashboardImageUpload.uploadFailed,
      copy.dashboardImageUpload.uploading,
      form.advanced_image_urls,
      propertyId,
      session,
      uploadSingleImageFile,
      workingPropertyId,
    ],
  );

  const onSubmit = async (event) => {
    event.preventDefault();

    if (!form.property_type) {
      toast.error(copy.dashboard.fieldRequired);
      return;
    }

    const areaValue = Number(form.area_sqm);
    if (!Number.isFinite(areaValue) || areaValue <= 0) {
      toast.error(copy.dashboard.invalidArea);
      return;
    }

    if (showsStructuredRoomFields) {
      const structuredFields = [
        [form.bedrooms, copy.dashboard.bedrooms],
        [form.bathrooms, copy.dashboard.bathrooms],
        [form.living_rooms, copy.dashboard.livingRooms],
        [form.parking_spots, copy.dashboard.parking],
      ];

      for (const [value, label] of structuredFields) {
        const parsed = Number(value);
        if (value === "" || !Number.isFinite(parsed) || parsed < 0) {
          toast.error(copy.dashboard.dynamicFieldRequired.replace("{field}", label));
          return;
        }
      }
    }

    if (showsFloorsCountField) {
      const parsedFloorsCount = Number(form.floors_count);
      if (form.floors_count === "" || !Number.isFinite(parsedFloorsCount) || parsedFloorsCount < 1) {
        toast.error(copy.dashboard.dynamicFieldRequired.replace("{field}", copy.dashboard.floorsCount));
        return;
      }
    }

    if (showsUnitsCountField && REQUIRED_UNITS_COUNT_PROPERTY_TYPES.has(form.property_type)) {
      const parsedUnitsCount = Number(form.units_count);
      if (form.units_count === "" || !Number.isFinite(parsedUnitsCount) || parsedUnitsCount < 1) {
        toast.error(copy.dashboard.dynamicFieldRequired.replace("{field}", unitsCountLabel));
        return;
      }
    }

    if (
      showsStreetFrontageField &&
      REQUIRED_STREET_FRONTAGE_PROPERTY_TYPES.has(form.property_type) &&
      !form.street_frontage_type
    ) {
      toast.error(copy.dashboard.dynamicFieldRequired.replace("{field}", copy.dashboard.streetFrontageType));
      return;
    }

    setSaving(true);
    setUploadState({ status: "idle", progress: 0, message: "" });
    setVideoUploadState({ status: "idle", progress: 0, message: "" });

    try {
      const payload = buildPayload();
      let workingItems = imageItemsRef.current.map((item) => ({ ...item }));

      const existingOrdered = workingItems
        .filter((item) => item.source)
        .map((item) => item.source);
      const manualUrls = parseAdvancedUrls(form.advanced_image_urls);
      payload.images = [...existingOrdered, ...manualUrls.filter((url) => !existingOrdered.includes(url))];

      const headers = { headers: buildAuthHeaders(session) };

      let targetId = workingPropertyId || propertyId;
      let latestImages = payload.images;
      let latestVideo = {
        video_url: payload.video_url,
        video_thumbnail_url: payload.video_thumbnail_url,
        video_preview_url: payload.video_preview_url,
      };

      if (targetId) {
        const updated = await api.put(`/admin/properties/${targetId}`, payload, headers);
        latestImages = updated.data?.images || latestImages;
        latestVideo = {
          video_url: updated.data?.video_url || latestVideo.video_url,
          video_thumbnail_url:
            updated.data?.video_thumbnail_url || latestVideo.video_thumbnail_url,
          video_preview_url:
            updated.data?.video_preview_url || latestVideo.video_preview_url,
        };
      } else {
        const created = await api.post("/admin/properties", payload, headers);
        targetId = created.data?.id;
        setWorkingPropertyId(targetId || "");
        latestImages = created.data?.images || latestImages;
        latestVideo = {
          video_url: created.data?.video_url || latestVideo.video_url,
          video_thumbnail_url:
            created.data?.video_thumbnail_url || latestVideo.video_thumbnail_url,
          video_preview_url:
            created.data?.video_preview_url || latestVideo.video_preview_url,
        };
      }

      const pendingItems = workingItems.filter((item) => item.file && !item.source);
      const totalUploads = pendingItems.length;
      let completedUploads = 0;
      let failedUploads = 0;

      if (targetId && pendingItems.length) {
        for (const pendingItem of pendingItems) {
          workingItems = workingItems.map((item) =>
            item.id === pendingItem.id
              ? { ...item, status: "uploading", errorMessage: "" }
              : item,
          );
          setImageItems(workingItems);

          try {
            const afterUploadImages = await uploadSingleImageFile(
              targetId,
              pendingItem.file,
              (fileProgress) => {
                const totalProgress = Math.min(
                  100,
                  Math.round(((completedUploads + fileProgress / 100) / totalUploads) * 100),
                );
                setUploadState({
                  status: "uploading",
                  progress: totalProgress,
                  message: copy.dashboardImageUpload.uploading,
                });
              },
            );

            const uploadedPaths = extractUploadedPaths(latestImages, afterUploadImages);
            const uploadedPath = uploadedPaths[0];
            if (!uploadedPath) {
              throw new Error("upload_failed");
            }

            latestImages = afterUploadImages;
            completedUploads += 1;

            if (pendingItem.previewUrl?.startsWith("blob:")) {
              URL.revokeObjectURL(pendingItem.previewUrl);
            }

            workingItems = workingItems.map((item) =>
              item.id === pendingItem.id
                ? {
                    ...item,
                    kind: "existing",
                    source: uploadedPath,
                    previewUrl: resolveImageUrl(uploadedPath),
                    file: null,
                    status: "success",
                    errorMessage: "",
                  }
                : item,
            );
            setImageItems(workingItems);
          } catch (error) {
            completedUploads += 1;
            failedUploads += 1;
            const detail =
              error?.response?.data?.detail || copy.dashboardImageUpload.uploadFailed;
            workingItems = workingItems.map((item) =>
              item.id === pendingItem.id
                ? { ...item, status: "failed", errorMessage: detail }
                : item,
            );
            setImageItems(workingItems);
          }
        }

        const finalImages = buildFinalOrderedImages(workingItems, manualUrls);

        if (!arraysEqual(finalImages, latestImages)) {
          await api.put(`/admin/properties/${targetId}`, { images: finalImages }, headers);
          latestImages = finalImages;
        }

        latestImages = finalImages;
        if (failedUploads > 0) {
          setUploadState({
            status: "error",
            progress: 100,
            message: copy.dashboardImageUpload.partialFailed,
          });
        } else {
          setUploadState({
            status: "success",
            progress: 100,
            message: copy.dashboardImageUpload.success,
          });
        }
      }

      if (targetId && videoPendingFile) {
        const updatedProperty = await uploadSelectedVideo(targetId, videoPendingFile);
        latestVideo = {
          video_url: updatedProperty?.video_url || latestVideo.video_url,
          video_thumbnail_url:
            updatedProperty?.video_thumbnail_url || latestVideo.video_thumbnail_url,
          video_preview_url: updatedProperty?.video_preview_url || latestVideo.video_preview_url,
        };
        setVideoUploadState({
          status: "success",
          progress: 100,
          message: copy.dashboardVideoUpload.success,
        });
        setVideoMarkedForRemoval(false);
        videoRemovalRef.current = false;
      }

      const hasFailedImage = workingItems.some((item) => item.status === "failed");

      if (hasFailedImage) {
        setVideoMarkedForRemoval(false);
        videoRemovalRef.current = false;
        toast.error(copy.dashboardImageUpload.partialFailed);
        setForm((prev) => ({
          ...prev,
          video_url: latestVideo.video_url || "",
          video_thumbnail_url: latestVideo.video_thumbnail_url || "",
          video_preview_url: latestVideo.video_preview_url || "",
        }));
        return;
      }

      toast.success(isEditMode ? copy.common.updateSuccess : copy.common.saveSuccess);

      const existingItems = buildExistingImageItems(latestImages);
      imageItemsRef.current.forEach((item) => revokePendingPreviewUrl(item));
      setImageItems(existingItems);
      revokeVideoPreviewUrl(videoPendingPreviewUrl);
      setVideoPendingPreviewUrl("");
      setVideoPendingFile(null);
      setVideoMarkedForRemoval(false);
      videoRemovalRef.current = false;
      setForm((prev) => ({
        ...prev,
        advanced_image_urls: "",
        video_url: latestVideo.video_url || "",
        video_thumbnail_url: latestVideo.video_thumbnail_url || "",
        video_preview_url: latestVideo.video_preview_url || "",
      }));
      goBackToList();
    } catch (error) {
      setUploadState({
        status: "error",
        progress: 0,
        message: error?.response?.data?.detail || copy.dashboardImageUpload.uploadFailed,
      });
      setVideoUploadState({
        status: "error",
        progress: 0,
        message: error?.response?.data?.detail || copy.dashboardVideoUpload.uploadFailed,
      });
      toast.error(error?.response?.data?.detail || copy.common.requestFailed);
    } finally {
      setSaving(false);
    }
  };

  const pendingVideoUrl = videoPendingFile ? videoPendingPreviewUrl : "";
  const effectiveVideoUrl = pendingVideoUrl || form.video_url;
  const effectiveVideoThumbnail = pendingVideoUrl || form.video_thumbnail_url || "";
  const resolvedEffectiveVideoUrl =
    effectiveVideoUrl && effectiveVideoUrl.startsWith("blob:")
      ? effectiveVideoUrl
      : resolveImageUrl(effectiveVideoUrl);

  if (loading) {
    return <p data-testid="dashboard-add-property-loading">{copy.common.loading}</p>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-add-property-page">
      <section className="space-y-2" data-testid="dashboard-add-property-header">
        <h1 className="text-3xl font-semibold text-stone-900 sm:text-4xl" data-testid="dashboard-add-property-title">
          {isEditMode ? copy.dashboardProperties.editProperty : copy.dashboardNav.addProperty}
        </h1>
        <p className="text-sm text-stone-600" data-testid="dashboard-add-property-subtitle">
          {copy.dashboardProperties.addEditSubtitle}
        </p>
      </section>

      <Card className="border-gold-500/20 bg-white/90" data-testid="dashboard-add-property-form-card">
        <CardHeader>
          <CardTitle data-testid="dashboard-add-property-form-title">
            {isEditMode ? copy.dashboard.editProperty : copy.dashboard.createProperty}
          </CardTitle>
        </CardHeader>

        <CardContent>
          <form className="grid gap-4 md:grid-cols-2" onSubmit={onSubmit} data-testid="dashboard-add-property-form">
            <div className="space-y-2">
              <label data-testid="dashboard-add-property-title-ar-label">{copy.dashboard.propertyTitleAr}</label>
              <Input value={form.title_ar} onChange={(event) => setForm((prev) => ({ ...prev, title_ar: event.target.value }))} required data-testid="dashboard-add-property-title-ar-input" />
            </div>
            <div className="space-y-2">
              <label data-testid="dashboard-add-property-title-en-label">{copy.dashboard.propertyTitleEn}</label>
              <Input value={form.title_en} onChange={(event) => setForm((prev) => ({ ...prev, title_en: event.target.value }))} required data-testid="dashboard-add-property-title-en-input" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label data-testid="dashboard-add-property-description-ar-label">{copy.dashboard.descriptionAr}</label>
              <Textarea value={form.description_ar} onChange={(event) => setForm((prev) => ({ ...prev, description_ar: event.target.value }))} required data-testid="dashboard-add-property-description-ar-input" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label data-testid="dashboard-add-property-description-en-label">{copy.dashboard.descriptionEn}</label>
              <Textarea value={form.description_en} onChange={(event) => setForm((prev) => ({ ...prev, description_en: event.target.value }))} required data-testid="dashboard-add-property-description-en-input" />
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-location-ar-label">{copy.dashboard.locationAr}</label>
              <Input value={form.location_ar} onChange={(event) => setForm((prev) => ({ ...prev, location_ar: event.target.value }))} required data-testid="dashboard-add-property-location-ar-input" />
            </div>
            <div className="space-y-2">
              <label data-testid="dashboard-add-property-location-en-label">{copy.dashboard.locationEn}</label>
              <Input value={form.location_en} onChange={(event) => setForm((prev) => ({ ...prev, location_en: event.target.value }))} required data-testid="dashboard-add-property-location-en-input" />
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-region-label">{copy.dashboard.region}</label>
              <Select value={form.region_code} onValueChange={(value) => setForm((prev) => ({ ...prev, region_code: value }))}>
                <SelectTrigger data-testid="dashboard-add-property-region-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {regions.map(([key, label]) => (
                    <SelectItem value={key} key={key} data-testid={`dashboard-add-property-region-option-${key}`}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-subarea-label">{copy.dashboard.subArea}</label>
              <Select value={form.sub_area_code} onValueChange={(value) => setForm((prev) => ({ ...prev, sub_area_code: value }))}>
                <SelectTrigger data-testid="dashboard-add-property-subarea-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(copy.subAreas).map(([key, label]) => (
                    <SelectItem value={key} key={key} data-testid={`dashboard-add-property-subarea-option-${key}`}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <PropertyLocationPickerSection
              lang={lang}
              copy={mapPickerCopy}
              regionCode={form.region_code}
              subAreaCode={form.sub_area_code}
              latitudeValue={form.latitude}
              longitudeValue={form.longitude}
              onCoordinatesChange={handleMapCoordinatesChange}
            />

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-listing-label">{copy.dashboard.listingType}</label>
              <Select value={form.listing_type} onValueChange={(value) => setForm((prev) => ({ ...prev, listing_type: value }))}>
                <SelectTrigger data-testid="dashboard-add-property-listing-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale" data-testid="dashboard-add-property-listing-sale">{copy.properties.sale}</SelectItem>
                  <SelectItem value="rent" data-testid="dashboard-add-property-listing-rent">{copy.properties.rent}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-type-label">{copy.dashboard.propertyType}</label>
              <Select
                value={form.property_type}
                onValueChange={(value) => setForm((prev) => ({ ...prev, property_type: value }))}
              >
                <SelectTrigger data-testid="dashboard-add-property-type-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPE_OPTIONS.map((typeKey) => (
                    <SelectItem value={typeKey} key={typeKey} data-testid={`dashboard-add-property-type-option-${typeKey}`}>
                      {propertyTypeLabels[typeKey] || typeKey}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-availability-label">{copy.dashboard.availability}</label>
              <Select value={form.availability} onValueChange={(value) => setForm((prev) => ({ ...prev, availability: value }))}>
                <SelectTrigger data-testid="dashboard-add-property-availability-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available" data-testid="dashboard-add-property-availability-available">{copy.properties.available}</SelectItem>
                  <SelectItem value="sold" data-testid="dashboard-add-property-availability-sold">{copy.properties.sold}</SelectItem>
                  <SelectItem value="rented" data-testid="dashboard-add-property-availability-rented">{copy.properties.rented}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-visibility-label">{copy.dashboard.visibility}</label>
              <Select value={form.is_visible ? "visible" : "hidden"} onValueChange={(value) => setForm((prev) => ({ ...prev, is_visible: value === "visible" }))}>
                <SelectTrigger data-testid="dashboard-add-property-visibility-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="visible" data-testid="dashboard-add-property-visibility-visible">{copy.dashboard.visible}</SelectItem>
                  <SelectItem value="hidden" data-testid="dashboard-add-property-visibility-hidden">{copy.dashboard.hidden}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-price-label">{copy.dashboard.price}</label>
              <Input type="number" min="1" value={form.price} onChange={(event) => setForm((prev) => ({ ...prev, price: event.target.value }))} required data-testid="dashboard-add-property-price-input" />
            </div>

            {showsStructuredRoomFields && (
              <>
                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-bedrooms-label">{copy.dashboard.bedrooms}</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.bedrooms}
                    onChange={(event) => setForm((prev) => ({ ...prev, bedrooms: event.target.value }))}
                    required
                    data-testid="dashboard-add-property-bedrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-bathrooms-label">{copy.dashboard.bathrooms}</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.bathrooms}
                    onChange={(event) => setForm((prev) => ({ ...prev, bathrooms: event.target.value }))}
                    required
                    data-testid="dashboard-add-property-bathrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-living-rooms-label">{copy.dashboard.livingRooms}</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.living_rooms}
                    onChange={(event) => setForm((prev) => ({ ...prev, living_rooms: event.target.value }))}
                    required
                    data-testid="dashboard-add-property-living-rooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-parking-label">{copy.dashboard.parking}</label>
                  <Input
                    type="number"
                    min="0"
                    value={form.parking_spots}
                    onChange={(event) => setForm((prev) => ({ ...prev, parking_spots: event.target.value }))}
                    required
                    data-testid="dashboard-add-property-parking-input"
                  />
                </div>
              </>
            )}

            {showsElevatorField && (
              <div className="space-y-2">
                <label data-testid="dashboard-add-property-has-elevator-label">{copy.dashboard.hasElevator}</label>
                <Select
                  value={
                    form.has_elevator === true
                      ? "yes"
                      : form.has_elevator === false
                        ? "no"
                        : "none"
                  }
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      has_elevator: value === "yes" ? true : value === "no" ? false : null,
                    }))
                  }
                >
                  <SelectTrigger data-testid="dashboard-add-property-has-elevator-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" data-testid="dashboard-add-property-has-elevator-option-none">
                      {copy.dashboard.optionalNone}
                    </SelectItem>
                    <SelectItem value="yes" data-testid="dashboard-add-property-has-elevator-option-yes">
                      {copy.dashboard.yes}
                    </SelectItem>
                    <SelectItem value="no" data-testid="dashboard-add-property-has-elevator-option-no">
                      {copy.dashboard.no}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {showsFloorsCountField && (
              <div className="space-y-2">
                <label data-testid="dashboard-add-property-floors-count-label">{copy.dashboard.floorsCount}</label>
                <Input
                  type="number"
                  min="1"
                  value={form.floors_count}
                  onChange={(event) => setForm((prev) => ({ ...prev, floors_count: event.target.value }))}
                  required
                  data-testid="dashboard-add-property-floors-count-input"
                />
              </div>
            )}

            {showsUnitsCountField && (
              <div className="space-y-2">
                <label data-testid="dashboard-add-property-units-count-label">{unitsCountLabel}</label>
                <Input
                  type="number"
                  min="1"
                  value={form.units_count}
                  onChange={(event) => setForm((prev) => ({ ...prev, units_count: event.target.value }))}
                  data-testid="dashboard-add-property-units-count-input"
                />
              </div>
            )}

            {showsPropertyAgeField && (
              <div className="space-y-2">
                <label data-testid="dashboard-add-property-property-age-label">{copy.dashboard.propertyAge}</label>
                <Input
                  type="number"
                  min="0"
                  value={form.property_age}
                  onChange={(event) => setForm((prev) => ({ ...prev, property_age: event.target.value }))}
                  data-testid="dashboard-add-property-property-age-input"
                />
              </div>
            )}

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-area-label">{isLandPropertyType ? copy.dashboard.landArea : copy.dashboard.area}</label>
              <Input type="number" min="1" value={form.area_sqm} onChange={(event) => setForm((prev) => ({ ...prev, area_sqm: event.target.value }))} required data-testid="dashboard-add-property-area-input" />
            </div>

            {showsStreetFrontageField && (
              <>
                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-street-frontage-label">{copy.dashboard.streetFrontageType}</label>
                  <Select
                    value={form.street_frontage_type || "none"}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        street_frontage_type: value === "none" ? "" : value,
                      }))
                    }
                  >
                    <SelectTrigger data-testid="dashboard-add-property-street-frontage-trigger"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="dashboard-add-property-street-frontage-option-none">
                        {copy.dashboard.optionalNone}
                      </SelectItem>
                      {STREET_FRONTAGE_OPTIONS.map((item) => (
                        <SelectItem
                          key={item}
                          value={item}
                          data-testid={`dashboard-add-property-street-frontage-option-${item}`}
                        >
                          {copy.dashboard.streetFrontageOptions?.[item] || item}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label data-testid="dashboard-add-property-zoning-label">{copy.dashboard.zoning}</label>
                  <Input
                    value={form.zoning}
                    onChange={(event) => setForm((prev) => ({ ...prev, zoning: event.target.value }))}
                    placeholder={copy.dashboard.zoningPlaceholder}
                    data-testid="dashboard-add-property-zoning-input"
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <label data-testid="dashboard-add-property-contact-label">{copy.dashboard.contactPhone}</label>
              <Input value={form.contact_phone} onChange={(event) => setForm((prev) => ({ ...prev, contact_phone: event.target.value }))} data-testid="dashboard-add-property-contact-input" />
            </div>

            <div className="space-y-2 md:col-span-2">
              <label className="text-sm font-medium text-stone-700" data-testid="dashboard-add-property-image-uploader-label">
                {copy.dashboardImageUpload.title}
              </label>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                multiple
                className="hidden"
                onChange={onFileInputChange}
                data-testid="dashboard-add-property-hidden-file-input"
              />

              <div
                className={`rounded-2xl border-2 border-dashed p-5 transition-colors ${dropzoneActive ? "border-gold-500 bg-amber-50/50" : "border-gold-500/30 bg-white"}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDropzoneActive(true);
                }}
                onDragLeave={() => setDropzoneActive(false)}
                onDrop={onDropFiles}
                data-testid="dashboard-add-property-dropzone"
              >
                <div className="flex flex-col items-center gap-3 text-center" data-testid="dashboard-add-property-dropzone-content">
                  <UploadCloud size={26} className="text-gold-700" />
                  <p className="text-sm text-stone-700" data-testid="dashboard-add-property-dropzone-title">
                    {copy.dashboardImageUpload.dropzoneTitle}
                  </p>
                  <p className="text-xs text-stone-500" data-testid="dashboard-add-property-dropzone-subtitle">
                    {copy.dashboardImageUpload.dropzoneSubtitle}
                  </p>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={processingFiles}
                    data-testid="dashboard-add-property-select-files-button"
                  >
                    {processingFiles ? copy.dashboardImageUpload.optimizing : copy.dashboardImageUpload.selectImages}
                  </Button>
                </div>
              </div>

              {imageItems.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="dashboard-add-property-preview-grid">
                  {imageItems.map((item, index) => (
                    <div
                      key={item.id}
                      draggable={item.status !== "uploading" && !saving}
                      onDragStart={() => setDraggingImageId(item.id)}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        moveImageItem(draggingImageId, item.id);
                        setDraggingImageId("");
                      }}
                      className="group overflow-hidden rounded-2xl border border-gold-500/20 bg-white shadow-sm"
                      data-testid={`dashboard-add-property-preview-item-${item.id}`}
                    >
                      <div className="relative aspect-[4/3] overflow-hidden" data-testid={`dashboard-add-property-preview-image-wrapper-${item.id}`}>
                        <img
                          src={item.previewUrl}
                          alt={item.name}
                          className="h-full w-full object-cover"
                          data-testid={`dashboard-add-property-preview-image-${item.id}`}
                        />

                        <div className="absolute left-2 top-2 flex items-center gap-1" data-testid={`dashboard-add-property-preview-top-badges-${item.id}`}>
                          {index === 0 && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-gold-600 px-2 py-1 text-[11px] text-stone-900" data-testid={`dashboard-add-property-cover-badge-${item.id}`}>
                              <Star size={12} /> {copy.dashboardImageUpload.cover}
                            </span>
                          )}

                          {item.status === "uploading" && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-stone-900/80 px-2 py-1 text-[11px] text-cream-50"
                              data-testid={`dashboard-add-property-uploading-badge-${item.id}`}
                            >
                              <Loader2 size={11} className="animate-spin" />
                              {copy.dashboardImageUpload.statusUploading || copy.dashboardImageUpload.uploading}
                            </span>
                          )}

                          {item.status === "success" && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-emerald-600/90 px-2 py-1 text-[11px] text-white"
                              data-testid={`dashboard-add-property-success-badge-${item.id}`}
                            >
                              <CheckCircle2 size={11} />
                              {copy.dashboardImageUpload.statusSuccess || copy.dashboardImageUpload.success}
                            </span>
                          )}

                          {item.status === "failed" && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-red-600/90 px-2 py-1 text-[11px] text-white"
                              data-testid={`dashboard-add-property-failed-badge-${item.id}`}
                            >
                              <AlertTriangle size={11} />
                              {copy.dashboardImageUpload.statusFailed || copy.dashboardImageUpload.uploadFailed}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 p-2" data-testid={`dashboard-add-property-preview-actions-${item.id}`}>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 rounded-full border border-gold-500/30 px-2 py-1 text-xs text-stone-700"
                          onClick={() => moveToCover(item.id)}
                          disabled={item.status === "uploading" || saving}
                          data-testid={`dashboard-add-property-make-cover-button-${item.id}`}
                        >
                          <ImagePlus size={12} /> {copy.dashboardImageUpload.setAsCover}
                        </button>

                        <div className="flex items-center gap-1">
                          {item.status === "failed" && (
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-full border border-amber-300 px-2 py-1 text-xs text-amber-700"
                              onClick={() => retryImageUpload(item.id)}
                              disabled={saving}
                              data-testid={`dashboard-add-property-retry-image-button-${item.id}`}
                            >
                              <RotateCcw size={12} />
                              {copy.dashboardImageUpload.retry || "Retry"}
                            </button>
                          )}

                          <span className="inline-flex items-center text-stone-400" data-testid={`dashboard-add-property-drag-handle-${item.id}`}>
                            <GripVertical size={14} />
                          </span>
                          <button
                            type="button"
                            className="inline-flex items-center rounded-full border border-red-300 px-2 py-1 text-xs text-red-600"
                            onClick={() => removeImageItem(item.id)}
                            disabled={item.status === "uploading" || saving}
                            data-testid={`dashboard-add-property-remove-image-button-${item.id}`}
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      {item.status === "failed" && item.errorMessage && (
                        <p
                          className="px-3 pb-2 text-xs text-red-700"
                          data-testid={`dashboard-add-property-image-error-${item.id}`}
                        >
                          {item.errorMessage}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2" data-testid="dashboard-add-property-upload-status-wrap">
                {(uploadState.status === "uploading" || uploadState.status === "success" || uploadState.status === "error") && (
                  <div className="rounded-xl border border-gold-500/20 bg-white p-3" data-testid="dashboard-add-property-upload-status-box">
                    <div className="mb-2 flex items-center justify-between text-xs text-stone-600" data-testid="dashboard-add-property-upload-status-header">
                      <span data-testid="dashboard-add-property-upload-status-text">{uploadState.message}</span>
                      {uploadState.status === "uploading" && <Loader2 size={14} className="animate-spin" />}
                    </div>
                    {uploadState.status === "uploading" && (
                      <div className="h-2 overflow-hidden rounded-full bg-stone-200" data-testid="dashboard-add-property-upload-progress-track">
                        <div
                          className="h-full bg-gold-600 transition-all"
                          style={{ width: `${uploadState.progress}%` }}
                          data-testid="dashboard-add-property-upload-progress-bar"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-gold-500/20 bg-amber-50/40" data-testid="dashboard-add-property-advanced-toggle-wrap">
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-stone-700"
                  onClick={() => setAdvancedOpen((prev) => !prev)}
                  data-testid="dashboard-add-property-advanced-toggle-button"
                >
                  <span>{copy.dashboardImageUpload.advancedToggle}</span>
                  <ChevronDown size={15} className={advancedOpen ? "rotate-180 transition-transform" : "transition-transform"} />
                </button>

                {advancedOpen && (
                  <div className="border-t border-gold-500/20 px-4 pb-4 pt-3" data-testid="dashboard-add-property-advanced-content">
                    <label className="text-xs text-stone-600" data-testid="dashboard-add-property-advanced-urls-label">
                      {copy.dashboard.imageUrls}
                    </label>
                    <Textarea
                      value={form.advanced_image_urls}
                      onChange={(event) => setForm((prev) => ({ ...prev, advanced_image_urls: event.target.value }))}
                      data-testid="dashboard-add-property-advanced-urls-input"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 md:col-span-2" data-testid="dashboard-add-property-video-section">
              <label className="text-sm font-medium text-stone-700" data-testid="dashboard-add-property-video-label">
                {copy.dashboardVideoUpload.title}
              </label>

              <input
                ref={videoFileInputRef}
                type="file"
                accept="video/mp4,video/quicktime,.mp4,.mov"
                className="hidden"
                onChange={onVideoInputChange}
                data-testid="dashboard-add-property-video-file-input"
              />

              <div
                className={`rounded-2xl border-2 border-dashed p-5 transition-colors ${videoDropzoneActive ? "border-gold-500 bg-amber-50/50" : "border-gold-500/30 bg-white"}`}
                onDragOver={(event) => {
                  event.preventDefault();
                  setVideoDropzoneActive(true);
                }}
                onDragLeave={() => setVideoDropzoneActive(false)}
                onDrop={onVideoDrop}
                data-testid="dashboard-add-property-video-dropzone"
              >
                <div className="flex flex-col items-center gap-3 text-center" data-testid="dashboard-add-property-video-dropzone-content">
                  <Video size={24} className="text-gold-700" />
                  <p className="text-sm text-stone-700" data-testid="dashboard-add-property-video-dropzone-title">
                    {copy.dashboardVideoUpload.dropzoneTitle}
                  </p>
                  <p className="text-xs text-stone-500" data-testid="dashboard-add-property-video-dropzone-subtitle">
                    {copy.dashboardVideoUpload.dropzoneSubtitle}
                  </p>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoFileInputRef.current?.click()}
                    data-testid="dashboard-add-property-video-select-button"
                  >
                    {copy.dashboardVideoUpload.selectVideo}
                  </Button>
                </div>
              </div>

              {(effectiveVideoUrl || effectiveVideoThumbnail) && (
                <div className="overflow-hidden rounded-2xl border border-gold-500/20 bg-white" data-testid="dashboard-add-property-video-preview-card">
                  <div className="relative aspect-video w-full bg-stone-100" data-testid="dashboard-add-property-video-preview-media">
                    {effectiveVideoThumbnail && (
                      <img
                        src={resolveImageUrl(effectiveVideoThumbnail)}
                        alt={copy.dashboardVideoUpload.thumbnailAlt}
                        className="absolute inset-0 h-full w-full object-cover"
                        data-testid="dashboard-add-property-video-thumbnail"
                      />
                    )}

                    {effectiveVideoUrl && (
                      <button
                        type="button"
                        onClick={() => setVideoPreviewModalOpen(true)}
                        className="absolute inset-0 flex items-center justify-center bg-black/25 text-cream-50 transition hover:bg-black/35"
                        data-testid="dashboard-add-property-video-open-preview-button"
                      >
                        <span className="inline-flex items-center gap-2 rounded-full bg-black/70 px-4 py-2 text-sm">
                          <PlayCircle size={16} /> {copy.dashboardVideoUpload.preview}
                        </span>
                      </button>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid="dashboard-add-property-video-actions">
                    <p className="text-xs text-stone-500" data-testid="dashboard-add-property-video-source-caption">
                      {videoPendingFile ? videoPendingFile.name : form.video_url}
                    </p>
                    <div className="flex items-center gap-2" data-testid="dashboard-add-property-video-action-buttons">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => videoFileInputRef.current?.click()}
                        data-testid="dashboard-add-property-video-replace-button"
                      >
                        {copy.dashboardVideoUpload.replace}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        onClick={removeVideoSelection}
                        data-testid="dashboard-add-property-video-remove-button"
                      >
                        {copy.dashboardVideoUpload.remove}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-3" data-testid="dashboard-add-property-video-urls-grid">
                <div className="space-y-1" data-testid="dashboard-add-property-video-url-field-wrap">
                  <label className="text-xs text-stone-600" data-testid="dashboard-add-property-video-url-label">
                    <span className="inline-flex items-center gap-1">
                      <Link2 size={12} /> {copy.dashboardVideoUpload.videoUrl}
                    </span>
                  </label>
                  <Input
                    value={form.video_url}
                    onChange={(event) => setForm((prev) => ({ ...prev, video_url: event.target.value }))}
                    placeholder="https://..."
                    data-testid="dashboard-add-property-video-url-input"
                  />
                </div>

                <div className="space-y-1" data-testid="dashboard-add-property-video-thumbnail-url-field-wrap">
                  <label className="text-xs text-stone-600" data-testid="dashboard-add-property-video-thumbnail-url-label">
                    {copy.dashboardVideoUpload.thumbnailUrl}
                  </label>
                  <Input
                    value={form.video_thumbnail_url}
                    onChange={(event) => setForm((prev) => ({ ...prev, video_thumbnail_url: event.target.value }))}
                    placeholder="https://..."
                    data-testid="dashboard-add-property-video-thumbnail-url-input"
                  />
                </div>

                <div className="space-y-1" data-testid="dashboard-add-property-video-preview-url-field-wrap">
                  <label className="text-xs text-stone-600" data-testid="dashboard-add-property-video-preview-url-label">
                    {copy.dashboardVideoUpload.previewUrl}
                  </label>
                  <Input
                    value={form.video_preview_url}
                    onChange={(event) => setForm((prev) => ({ ...prev, video_preview_url: event.target.value }))}
                    placeholder="https://..."
                    data-testid="dashboard-add-property-video-preview-url-input"
                  />
                </div>
              </div>

              {(videoUploadState.status === "uploading" ||
                videoUploadState.status === "success" ||
                videoUploadState.status === "error") && (
                <div className="rounded-xl border border-gold-500/20 bg-white p-3" data-testid="dashboard-add-property-video-status-box">
                  <div className="mb-2 flex items-center justify-between text-xs text-stone-600" data-testid="dashboard-add-property-video-status-header">
                    <span data-testid="dashboard-add-property-video-status-text">{videoUploadState.message}</span>
                    {videoUploadState.status === "uploading" && <Loader2 size={14} className="animate-spin" />}
                  </div>
                  {videoUploadState.status === "uploading" && (
                    <div className="h-2 overflow-hidden rounded-full bg-stone-200" data-testid="dashboard-add-property-video-progress-track">
                      <div
                        className="h-full bg-gold-600 transition-all"
                        style={{ width: `${videoUploadState.progress}%` }}
                        data-testid="dashboard-add-property-video-progress-bar"
                      />
                    </div>
                  )}
                </div>
              )}

              <Dialog open={videoPreviewModalOpen} onOpenChange={setVideoPreviewModalOpen}>
                <DialogContent className="max-w-4xl border border-gold-500/20 bg-stone-950 p-2" data-testid="dashboard-add-property-video-preview-dialog">
                  <DialogHeader className="px-4 pb-0 pt-2 text-start">
                    <DialogTitle className="text-sm text-cream-50" data-testid="dashboard-add-property-video-preview-dialog-title">
                      {copy.dashboardVideoUpload.preview}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-cream-200" data-testid="dashboard-add-property-video-preview-dialog-description">
                      {copy.dashboardVideoUpload.previewDescription}
                    </DialogDescription>
                  </DialogHeader>

                  {effectiveVideoUrl ? (
                    <video
                      key={effectiveVideoUrl}
                      controls
                      autoPlay
                      preload="metadata"
                      poster={effectiveVideoThumbnail ? resolveImageUrl(effectiveVideoThumbnail) : undefined}
                      className="aspect-video w-full rounded-xl object-contain"
                      src={resolvedEffectiveVideoUrl}
                      data-testid="dashboard-add-property-video-preview-player"
                    />
                  ) : (
                    <p className="px-4 pb-4 text-xs text-cream-200" data-testid="dashboard-add-property-video-preview-empty">
                      {copy.dashboardVideoUpload.noVideo}
                    </p>
                  )}
                </DialogContent>
              </Dialog>
            </div>

            <div className="flex flex-wrap gap-3 md:col-span-2" data-testid="dashboard-add-property-actions">
              <Button type="submit" disabled={saving} data-testid="dashboard-add-property-submit-button">
                {saving ? copy.common.loading : isEditMode ? copy.dashboard.update : copy.dashboard.save}
              </Button>
              <Button type="button" variant="outline" onClick={goBackToList} data-testid="dashboard-add-property-cancel-button">
                {copy.dashboardProperties.backToProperties}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
